import React from 'react';

import GreenCheckMarkBadge from 'src/assets/green-check-mark-badge';
import useUI from 'src/hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import {
  PdpIncentiveAccordionBanner,
  HeadlineRow,
  HeadlineText,
  LinkBankButton,
  BankLogoStripPlaceholder,
} from 'src/payments/components/incentive-accordion-styles/incentive-accordion-styles';
import { PointsBoostDetails } from 'src/payments/components/points-boost-incentive-banner';
import { useCashBackSurfaceGate } from 'src/payments/hooks/use-cash-back-surface-gate';
import { useDutchiePay } from 'src/payments/hooks/use-dutchie-pay/use-dutchie-pay';
import { useIncentiveExposure } from 'src/payments/hooks/use-incentive-exposure';
import { useIncentives } from 'src/payments/hooks/use-incentives/use-incentives';
import { useOpenDutchiePay } from 'src/payments/hooks/use-open-dutchie-pay/use-open-dutchie-pay';
import { usePayByBankPointsBoost } from 'src/payments/hooks/use-pay-by-bank-points-boost';
import { usePointsBoostSurfacesConfig } from 'src/payments/hooks/use-points-boost-surfaces-config';
import { Typography } from 'src/typography';

const BankLogosMarqueeVertical = React.lazy(() =>
  import('src/payments/components/bank-logos-marquee-vertical').then((module) => ({
    default: module.BankLogosMarqueeVertical,
  }))
);

// The CTA lives inside the accordion summary's mainContent; the summary itself is the disclosure
// control, so every interaction path (click, Space, Enter) must stop propagation to avoid
// toggling the accordion when the CTA is used.
const stopSummaryToggle = (event: React.SyntheticEvent): void => event.stopPropagation();

export const PdpPointsBoostAccordion = (): JSX.Element | null => {
  const UI = useUI();
  const { dispensary } = useDispensary();
  const { productDescription: pointsBoostPdpEnabled } = usePointsBoostSurfacesConfig();
  const { showCashBack } = useCashBackSurfaceGate('productDescription');
  const { isEnrolledDutchiePay, isDutchiePayEnabledForDispo } = useDutchiePay();
  const { userType } = useIncentives();
  const { openDutchiePay } = useOpenDutchiePay({
    enrollmentSource: `pdp-points-boost-${UI?.variant}`,
  });

  // Cash Back wins the slot when both are eligible, so at most one reward accordion renders.
  const enabled = pointsBoostPdpEnabled && !showCashBack && !UI?.isKiosk && isDutchiePayEnabledForDispo;

  const { status, multiplierLabel } = usePayByBankPointsBoost({
    dispensaryId: dispensary?.id,
    enabled,
  });

  // Undefined while hidden resets the hook's dedup ref so a re-shown accordion re-fires.
  useIncentiveExposure({
    incentiveValue: enabled && status === 'available' ? multiplierLabel ?? undefined : undefined,
    userType,
    isCashBack: false,
    source: 'product_description',
    rewardType: 'points_boost',
  });

  if (!enabled || status !== 'available' || multiplierLabel === null) {
    return null;
  }

  const mainContent = isEnrolledDutchiePay ? (
    <HeadlineText>
      <strong>Bank linked</strong>
      <br />
      Pay by Bank at checkout to get <strong>{multiplierLabel} points</strong>. <u>How it works.</u>
    </HeadlineText>
  ) : (
    <HeadlineRow>
      <HeadlineText>
        Get <strong>{multiplierLabel} points</strong> on all orders paid by bank! <u>How it works.</u>
      </HeadlineText>
      <LinkBankButton
        type='button'
        onClick={(event) => {
          stopSummaryToggle(event);
          openDutchiePay();
        }}
        onKeyDown={stopSummaryToggle}
        onKeyUp={stopSummaryToggle}
      >
        Link bank
      </LinkBankButton>
    </HeadlineRow>
  );

  const toggleContent = isEnrolledDutchiePay ? (
    <Typography.Label size='small'>
      Your bank is linked. Choose Pay now and complete your Pay by Bank payment at checkout to get {multiplierLabel}{' '}
      loyalty points.
    </Typography.Label>
  ) : (
    <PointsBoostDetails multiplierLabel={multiplierLabel} />
  );

  return (
    <PdpIncentiveAccordionBanner
      marginBottom='16px'
      borderRadius='var(--dcm-pdp-incentive-radius, 4px)'
      graphic={
        isEnrolledDutchiePay ? (
          <GreenCheckMarkBadge />
        ) : (
          <React.Suspense fallback={<BankLogoStripPlaceholder />}>
            <BankLogosMarqueeVertical />
          </React.Suspense>
        )
      }
      graphicWidth={isEnrolledDutchiePay ? undefined : '20px'}
      graphicFullHeight={!isEnrolledDutchiePay}
      mainContent={mainContent}
      toggleContent={toggleContent}
    />
  );
};
